// src/api/entitlement.js
// Lightweight entitlement token storage + axios header propagation.

import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const ENTITLEMENT_STORAGE_KEY = "tradepilot.entitlement.token";

let entitlementInterceptorInstalled = false;

function parseJwtPayload(token) {
  const parts = String(token || "").split(".");
  if (parts.length !== 3) {
    return null;
  }
  try {
    const payloadRaw = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payloadRaw + "=".repeat((4 - (payloadRaw.length % 4)) % 4);
    const decoded =
      typeof atob === "function"
        ? atob(padded)
        : Buffer.from(padded, "base64").toString("utf-8");
    const parsed = JSON.parse(decoded);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function tokenIsExpired(token) {
  const payload = parseJwtPayload(token);
  const exp = Number(payload?.exp || 0);
  if (!exp) {
    return false;
  }
  const nowSeconds = Math.floor(Date.now() / 1000);
  return nowSeconds >= exp;
}

export function clearEntitlementToken() {
  try {
    window.localStorage.removeItem(ENTITLEMENT_STORAGE_KEY);
  } catch {
    // Ignore storage failures in restricted environments.
  }
}

export function setEntitlementToken(token) {
  const normalized = String(token || "").trim();
  if (!normalized) {
    clearEntitlementToken();
    return;
  }
  try {
    window.localStorage.setItem(ENTITLEMENT_STORAGE_KEY, normalized);
  } catch {
    // Ignore storage failures in restricted environments.
  }
}

export function getEntitlementToken() {
  let token = "";
  try {
    token = String(window.localStorage.getItem(ENTITLEMENT_STORAGE_KEY) || "").trim();
  } catch {
    token = "";
  }

  if (!token) {
    return "";
  }
  if (tokenIsExpired(token)) {
    clearEntitlementToken();
    return "";
  }
  return token;
}

function shouldAttachEntitlementHeader(configUrl) {
  const rawUrl = String(configUrl || "").trim();
  if (!rawUrl) {
    return false;
  }
  if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) {
    return rawUrl.startsWith(API_BASE_URL);
  }
  // Relative URLs are assumed to target API base in this app.
  return true;
}

export function ensureEntitlementAxiosInterceptor() {
  if (entitlementInterceptorInstalled) {
    return;
  }

  axios.interceptors.request.use((config) => {
    const token = getEntitlementToken();
    if (!token) {
      return config;
    }

    if (!shouldAttachEntitlementHeader(config?.url)) {
      return config;
    }

    const headers = config.headers || {};
    return {
      ...config,
      headers: {
        ...headers,
        Authorization: `Bearer ${token}`,
      },
    };
  });

  entitlementInterceptorInstalled = true;
}
